"use client";
import { useEffect, useRef, useState } from "react";

interface UseInViewOptions {
  margin?: string;
  threshold?: number;
  triggerOnce?: boolean;
}

/**
 * Hook to detect when an element is in the viewport
 * Replacement for Framer Motion's useInView
 */
export const useInView = <T extends HTMLElement = HTMLElement>(
  ref: React.RefObject<T>,
  options: UseInViewOptions = {},
): boolean => {
  const [inView, setInView] = useState(false);
  const hasTriggered = useRef(false);

  const { margin = "0px", threshold = 0, triggerOnce = false } = options;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!triggerOnce || !hasTriggered.current) {
              setInView(true);
              hasTriggered.current = true;
            }
          } else if (!triggerOnce) {
            setInView(false);
          }
        });
      },
      {
        rootMargin: margin,
        threshold,
      },
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [ref, margin, threshold, triggerOnce]);

  return inView;
};
